import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Plus, ArrowRight, Power, Trash2, TestTube, Shield, ChevronRight, Loader2, X, Copy, Check, ExternalLink, Clock, Pencil, Save, Activity, Search, Filter } from 'lucide-react';
import { getPipelines, createPipeline, deletePipeline, togglePipeline, testPipeline, updatePipeline, getContracts, getPipelineEvents } from '../api';
import { getConnectorById, isConnectorAvailable, CONNECTORS } from '../connectorRegistry';
import ConnectorCatalog, { ConnectorFields } from './ConnectorCatalog';
import Modal from './Modal';
import logo_webhook from '../images/logos/webhook.png';

// ---------------------------------------------------------------------------
// Pipeline Card
// ---------------------------------------------------------------------------

function PipelineCard({ pipeline, onToggle, onDelete, onClick, addToast }) {
    const [testing, setTesting] = useState(false);

    const handleTest = async (e) => {
        e.stopPropagation();
        setTesting(true);
        try {
            const result = await testPipeline(pipeline.id);
            addToast(result.success ? `✅ ${result.message}` : `❌ ${result.message}`, result.success ? 'success' : 'error');
        } catch {
            addToast('Test failed', 'error');
        } finally {
            setTesting(false);
        }
    };

    const source = pipeline.source || {};
    const contract = pipeline.contract || {};
    const destination = pipeline.destination;

    const sourceConnector = getConnectorById(source.type || source.provider);
    const destConnector = destination ? getConnectorById(destination.type) : null;

    const sourceName = sourceConnector?.name || (source.type || '').toUpperCase();
    const sourceLogo = sourceConnector?.logo || logo_webhook;
    const destName = destConnector?.name || (destination ? 'Export' : 'Monitor Only');
    const destLogo = destConnector?.logo || logo_webhook;

    return (
        <div
            onClick={onClick}
            className={`bg-[#252526] border rounded-lg p-4 transition-all cursor-pointer hover:border-[#555] ${pipeline.is_active ? 'border-[#3c3c3c]' : 'border-[#3c3c3c] opacity-50'}`}
        >
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-white truncate">{pipeline.name}</h3>
                <div className="flex items-center gap-1.5">
                    <button onClick={handleTest} disabled={testing} className="p-1.5 rounded hover:bg-[#3c3c3c] text-[#808080] hover:text-emerald-400 transition-colors" title="Test source">
                        {testing ? <Loader2 size={14} className="animate-spin" /> : <TestTube size={14} />}
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); onToggle(pipeline.id); }} className={`p-1.5 rounded hover:bg-[#3c3c3c] transition-colors ${pipeline.is_active ? 'text-green-400' : 'text-[#808080]'}`} title={pipeline.is_active ? 'Pause' : 'Activate'}>
                        <Power size={14} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); onDelete(pipeline.id); }} className="p-1.5 rounded hover:bg-[#3c3c3c] text-[#808080] hover:text-red-400 transition-colors" title="Delete">
                        <Trash2 size={14} />
                    </button>
                </div>
            </div>

            {/* Visual Flow: [Source] → [Destination] */}
            <div className="flex items-center gap-2 text-xs">
                {/* Source Node */}
                <div className="flex items-center gap-1.5 bg-[#1e1e1e] border border-[#3c3c3c] rounded-md px-2.5 py-1.5 min-w-0 shrink">
                    <img src={sourceLogo} alt={sourceName} className="w-4 h-4 object-contain rounded-sm shrink-0" />
                    <span className="text-[#cccccc] truncate">{sourceName}</span>
                </div>

                <ArrowRight size={14} className="text-[#555] shrink-0" />

                {/* Destination Node */}
                <div className="flex items-center gap-1.5 bg-[#1e1e1e] border border-[#3c3c3c] rounded-md px-2.5 py-1.5 min-w-0 shrink">
                    <img src={destination ? destLogo : logo_webhook} alt={destName} className="w-4 h-4 object-contain rounded-sm shrink-0" />
                    <span className="text-[#a0a0a0] truncate">{destName}</span>
                </div>
            </div>

            {/* Stats strip */}
            <div className="flex items-center gap-4 mt-3 text-[11px] text-[#808080]">
                <span>{pipeline.is_active ? <span className="text-green-400">● Active</span> : <span className="text-[#555]">● Paused</span>}</span>
                <span>{pipeline.total_events || 0} events</span>
                {pipeline.last_event_at && <span>Last: {new Date(pipeline.last_event_at).toLocaleString()}</span>}
                {source.trigger_count > 0 && <span>{source.trigger_count} triggers</span>}
                {source.files_processed > 0 && <span>{source.files_processed} files</span>}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Pipeline Detail Panel (Slide-out)
// ---------------------------------------------------------------------------

function CopyButton({ value, label }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* fallback */ }
    };

    return (
        <button
            onClick={handleCopy}
            className="flex items-center gap-1 text-[10px] text-[#808080] hover:text-white transition-colors"
            title={`Copy ${label}`}
        >
            {copied ? <Check size={10} className="text-emerald-400" /> : <Copy size={10} />}
            {copied ? 'Copied' : 'Copy'}
        </button>
    );
}

function DetailRow({ label, value, copyable, mono }) {
    if (!value && value !== 0) return null;
    return (
        <div className="flex items-start justify-between gap-4 py-2 border-b border-[#2a2a2a] last:border-0">
            <span className="text-xs text-[#808080] shrink-0">{label}</span>
            <div className="flex items-center gap-2 min-w-0">
                <span className={`text-xs text-[#cccccc] truncate text-right ${mono ? 'font-mono text-[11px]' : ''}`}>{value}</span>
                {copyable && <CopyButton value={String(value)} label={label} />}
            </div>
        </div>
    );
}

function PipelineDetailPanel({ pipeline, onClose, onUpdate, addToast }) {
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(pipeline.name);
    const [description, setDescription] = useState(pipeline.description || '');
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [events, setEvents] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(true);

    // Reset when pipeline changes
    useEffect(() => {
        setName(pipeline.name);
        setDescription(pipeline.description || '');
        setEditing(false);
        // Fetch activity events for this pipeline's contract
        if (pipeline.contract?.id) {
            setLoadingEvents(true);
            getPipelineEvents(pipeline.contract.id, 10)
                .then(data => setEvents(data.items || []))
                .catch(() => setEvents([]))
                .finally(() => setLoadingEvents(false));
        } else {
            setEvents([]);
            setLoadingEvents(false);
        }
    }, [pipeline.id]);

    const source = pipeline.source || {};
    const contract = pipeline.contract || {};
    const destination = pipeline.destination;

    const sourceConnector = getConnectorById(source.type || source.provider);
    const destConnector = destination ? getConnectorById(destination.type) : null;

    const sourceName = sourceConnector?.name || (source.type || '').toUpperCase();
    const sourceLogo = sourceConnector?.logo || logo_webhook;

    const isPush = ['webhook', 'as2'].includes(source.type);

    const handleSave = async () => {
        setSaving(true);
        try {
            const updated = await updatePipeline(pipeline.id, { name, description: description || null });
            addToast('Pipeline updated', 'success');
            onUpdate(updated);
            setEditing(false);
        } catch {
            addToast('Failed to update pipeline', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        setTesting(true);
        try {
            const result = await testPipeline(pipeline.id);
            addToast(result.success ? `✅ ${result.message}` : `❌ ${result.message}`, result.success ? 'success' : 'error');
        } catch {
            addToast('Test failed', 'error');
        } finally {
            setTesting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />

            {/* Panel */}
            <div className="relative w-full max-w-lg bg-[#1e1e1e] border-l border-[#3c3c3c] shadow-2xl overflow-y-auto animate-slideIn">
                {/* Header */}
                <div className="sticky top-0 bg-[#1e1e1e] border-b border-[#3c3c3c] px-5 py-4 z-10 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                        <img src={sourceLogo} alt={sourceName} className="w-6 h-6 object-contain rounded shrink-0" />
                        <div className="min-w-0">
                            {editing ? (
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="bg-[#252526] border border-[#3c3c3c] rounded px-2 py-0.5 text-white text-sm font-semibold w-full"
                                    autoFocus
                                />
                            ) : (
                                <h3 className="text-sm font-semibold text-white truncate">{pipeline.name}</h3>
                            )}
                            <p className="text-[10px] text-[#808080] flex items-center gap-1.5 mt-0.5">
                                {pipeline.is_active
                                    ? <><span className="text-green-400">●</span> Active</>
                                    : <><span className="text-[#555]">●</span> Paused</>
                                }
                                <span className="text-[#3c3c3c]">·</span>
                                Created {new Date(pipeline.created_at).toLocaleDateString()}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                        {editing ? (
                            <>
                                <button
                                    onClick={handleSave}
                                    disabled={saving || !name.trim()}
                                    className="p-1.5 rounded hover:bg-[#3c3c3c] text-emerald-400 transition-colors"
                                    title="Save"
                                >
                                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                </button>
                                <button onClick={() => { setEditing(false); setName(pipeline.name); setDescription(pipeline.description || ''); }} className="p-1.5 rounded hover:bg-[#3c3c3c] text-[#808080] transition-colors">
                                    <X size={16} />
                                </button>
                            </>
                        ) : (
                            <button onClick={() => setEditing(true)} className="p-1.5 rounded hover:bg-[#3c3c3c] text-[#808080] hover:text-white transition-colors" title="Edit">
                                <Pencil size={16} />
                            </button>
                        )}
                        <button onClick={onClose} className="p-1.5 rounded hover:bg-[#3c3c3c] text-[#808080] hover:text-white transition-colors">
                            <X size={16} />
                        </button>
                    </div>
                </div>

                <div className="px-5 py-4 space-y-5">
                    {/* Description */}
                    {editing ? (
                        <div>
                            <label className="block text-xs text-[#808080] mb-1">Description</label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={2}
                                className="w-full p-2 bg-[#252526] border border-[#3c3c3c] rounded-lg text-white text-sm resize-none"
                                placeholder="What does this pipeline do?"
                            />
                        </div>
                    ) : pipeline.description ? (
                        <p className="text-xs text-[#a0a0a0] leading-relaxed">{pipeline.description}</p>
                    ) : null}

                    {/* ── Source Section ── */}
                    <div>
                        <h4 className="text-xs font-semibold text-[#808080] uppercase tracking-wider mb-2">Source</h4>
                        <div className="bg-[#252526] rounded-lg border border-[#3c3c3c] p-3">
                            <div className="flex items-center gap-2 mb-2">
                                <img src={sourceLogo} alt={sourceName} className="w-5 h-5 object-contain rounded-sm" />
                                <span className="text-sm font-medium text-white">{sourceName}</span>
                                {sourceConnector?.status === 'available' && (
                                    <span className="text-[9px] font-medium text-emerald-400 bg-emerald-900/20 border border-emerald-800/30 rounded px-1.5 py-0.5">Ready</span>
                                )}
                            </div>

                            {/* Push source — show endpoint URLs */}
                            {isPush && (
                                <div className="space-y-0">
                                    {source.type === 'webhook' && source.webhook_url && (
                                        <DetailRow label="Webhook URL" value={source.webhook_url} copyable mono />
                                    )}
                                    {source.type === 'as2' && source.as2_url && (
                                        <DetailRow label="AS2 URL" value={source.as2_url} copyable mono />
                                    )}
                                    {source.webhook_token && (
                                        <DetailRow label="Token" value={`${source.webhook_token.slice(0, 12)}...`} copyable={false} mono />
                                    )}
                                    <DetailRow label="Triggers" value={source.trigger_count || 0} />
                                    {source.last_triggered_at && (
                                        <DetailRow label="Last Triggered" value={new Date(source.last_triggered_at).toLocaleString()} />
                                    )}
                                </div>
                            )}

                            {/* Pull source — show connection details */}
                            {!isPush && (
                                <div className="space-y-0">
                                    <DetailRow label="Provider" value={source.provider?.toUpperCase()} />
                                    <DetailRow label="Bucket / Host" value={source.bucket_name} copyable mono />
                                    {source.prefix && <DetailRow label="Prefix" value={source.prefix} mono />}
                                    <DetailRow label="File Pattern" value={source.file_pattern} mono />
                                    <DetailRow label="Files Processed" value={source.files_processed || 0} />
                                    {source.last_polled_at && (
                                        <DetailRow label="Last Polled" value={new Date(source.last_polled_at).toLocaleString()} />
                                    )}
                                    <DetailRow
                                        label="Health"
                                        value={
                                            source.health_status === 'healthy' ? '● Healthy' :
                                                source.health_status === 'error' ? '● Error' : '● Unknown'
                                        }
                                    />
                                </div>
                            )}

                            {/* Test Connection Button */}
                            <button
                                onClick={handleTest}
                                disabled={testing}
                                className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#cccccc] bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg hover:border-emerald-600 hover:text-emerald-400 transition-colors"
                            >
                                {testing ? <Loader2 size={12} className="animate-spin" /> : <TestTube size={12} />}
                                Test Connection
                            </button>
                        </div>
                    </div>

                    {/* ── Contract Section ── */}
                    <div>
                        <h4 className="text-xs font-semibold text-[#808080] uppercase tracking-wider mb-2">Contract</h4>
                        <div className="bg-[#252526] rounded-lg border border-emerald-800/30 p-3 flex items-center gap-3">
                            <Shield size={18} className="text-emerald-400 shrink-0" />
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-emerald-300 truncate">{contract.name || 'Unknown'}</p>
                                <p className="text-[10px] text-[#808080]">Contract ID: {contract.id}</p>
                            </div>
                        </div>
                    </div>

                    {/* ── Destination Section ── */}
                    <div>
                        <h4 className="text-xs font-semibold text-[#808080] uppercase tracking-wider mb-2">Destination</h4>
                        {destination ? (
                            <div className="bg-[#252526] rounded-lg border border-[#3c3c3c] p-3">
                                <div className="flex items-center gap-2">
                                    <img
                                        src={destConnector?.logo || logo_webhook}
                                        alt={destConnector?.name || destination.type}
                                        className="w-5 h-5 object-contain rounded-sm"
                                    />
                                    <span className="text-sm font-medium text-white">
                                        {destConnector?.name || destination.type}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-[#252526] rounded-lg border border-dashed border-[#3c3c3c] p-3 text-center">
                                <p className="text-xs text-[#555]">No export destination — monitor-only mode</p>
                            </div>
                        )}
                    </div>

                    {/* ── Stats Section ── */}
                    <div>
                        <h4 className="text-xs font-semibold text-[#808080] uppercase tracking-wider mb-2">Statistics</h4>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="bg-[#252526] rounded-lg border border-[#3c3c3c] p-3 text-center">
                                <p className="text-lg font-bold text-white">{pipeline.total_events || 0}</p>
                                <p className="text-[10px] text-[#808080]">Total Events</p>
                            </div>
                            <div className="bg-[#252526] rounded-lg border border-[#3c3c3c] p-3 text-center">
                                <p className="text-lg font-bold text-white">
                                    {pipeline.last_event_at ? new Date(pipeline.last_event_at).toLocaleDateString() : '—'}
                                </p>
                                <p className="text-[10px] text-[#808080]">Last Event</p>
                            </div>
                        </div>
                    </div>

                    {/* ── Activity Log ── */}
                    <div>
                        <h4 className="text-xs font-semibold text-[#808080] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                            <Activity size={12} />
                            Recent Activity
                        </h4>
                        {loadingEvents ? (
                            <div className="flex items-center justify-center py-4">
                                <Loader2 size={14} className="animate-spin text-[#808080]" />
                            </div>
                        ) : events.length === 0 ? (
                            <div className="bg-[#252526] rounded-lg border border-dashed border-[#3c3c3c] p-4 text-center">
                                <p className="text-xs text-[#555]">No drift events yet</p>
                            </div>
                        ) : (
                            <div className="space-y-1.5">
                                {events.map(evt => {
                                    const statusColor = evt.status === 'resolved' ? 'text-emerald-400' : evt.status === 'ignored' ? 'text-[#555]' : 'text-yellow-400';
                                    const severityBg = evt.severity === 'major' ? 'bg-red-900/20 text-red-400 border-red-800/30' : evt.severity === 'minor' ? 'bg-yellow-900/20 text-yellow-400 border-yellow-800/30' : 'bg-[#2a2a2a] text-[#808080] border-[#3c3c3c]';
                                    const totalChanges = (evt.addition_count || 0) + (evt.removal_count || 0) + (evt.rename_count || 0) + (evt.change_count || 0);

                                    return (
                                        <div key={evt.id} className="flex items-center gap-2 bg-[#252526] border border-[#3c3c3c] rounded-md px-3 py-2">
                                            <span className={`text-[10px] ${statusColor}`}>●</span>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-[#cccccc] truncate">{evt.source_name}</p>
                                                <p className="text-[10px] text-[#808080]">
                                                    {totalChanges} change{totalChanges !== 1 ? 's' : ''} · {new Date(evt.created_at).toLocaleDateString()}
                                                </p>
                                            </div>
                                            <span className={`text-[9px] font-medium rounded px-1.5 py-0.5 border ${severityBg}`}>
                                                {evt.severity}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* ── Timestamps ── */}
                    <div className="text-[10px] text-[#555] space-y-1 pt-2 border-t border-[#2a2a2a]">
                        <p>Created: {new Date(pipeline.created_at).toLocaleString()}</p>
                        <p>Updated: {new Date(pipeline.updated_at).toLocaleString()}</p>
                        <p>Pipeline ID: {pipeline.id}</p>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes slideIn {
                    from { transform: translateX(100%); }
                    to   { transform: translateX(0); }
                }
                .animate-slideIn { animation: slideIn 0.2s ease-out; }
            `}</style>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Create Pipeline Wizard (with ConnectorCatalog)
// ---------------------------------------------------------------------------

function CreatePipelineWizard({ isOpen, onClose, onCreated, contracts, addToast }) {
    const [step, setStep] = useState(1);
    const [name, setName] = useState('');
    const [sourceId, setSourceId] = useState(null);
    const [sourceFields, setSourceFields] = useState({});
    const [contractId, setContractId] = useState('');
    const [destId, setDestId] = useState(null);
    const [destFields, setDestFields] = useState({});
    const [creating, setCreating] = useState(false);

    const reset = () => {
        setStep(1); setName(''); setSourceId(null); setSourceFields({});
        setContractId(''); setDestId(null); setDestFields({}); setCreating(false);
    };

    const handleClose = () => { reset(); onClose(); };

    const handleSourceFieldChange = (key, value) => {
        setSourceFields(prev => ({ ...prev, [key]: value }));
    };

    const handleDestFieldChange = (key, value) => {
        setDestFields(prev => ({ ...prev, [key]: value }));
    };

    const sourceConnector = sourceId ? getConnectorById(sourceId) : null;

    const buildSourceConfig = () => {
        if (!sourceConnector) return {};
        const fields = sourceFields;

        const bucket_name = fields.bucket_name || fields.host || '/';
        const prefix = fields.prefix || undefined;
        const file_pattern = fields.file_pattern || '*.json';
        const poll_interval_minutes = fields.poll_interval_minutes ? parseInt(fields.poll_interval_minutes) : 60;

        const credentialKeys = (sourceConnector.fields || [])
            .filter(f => !['bucket_name', 'prefix', 'file_pattern', 'poll_interval_minutes'].includes(f.key))
            .map(f => f.key);

        const credentials = {};
        credentialKeys.forEach(k => {
            if (fields[k] !== undefined && fields[k] !== '') credentials[k] = fields[k];
        });

        return { bucket_name, prefix, file_pattern, poll_interval_minutes, credentials };
    };

    const canProceedStep1 = name && sourceId;
    const canProceedStep2 = contractId;

    const handleCreate = async () => {
        setCreating(true);
        try {
            const data = {
                name,
                source_type: sourceId,
                contract_id: parseInt(contractId),
                source_config: buildSourceConfig(),
                destination_type: destId,
                destination_config: destFields,
            };
            await createPipeline(data);
            addToast('Pipeline created successfully!', 'success');
            onCreated();
            handleClose();
        } catch (err) {
            addToast(err.response?.data?.detail || 'Failed to create pipeline', 'error');
        } finally {
            setCreating(false);
        }
    };

    const isPullSource = sourceId && !['webhook', 'as2'].includes(sourceId);

    const stepTitles = ['Source', 'Contract', 'Destination'];

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="New Pipeline"
            maxWidth="max-w-4xl"
            footer={
                <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-1.5">
                        {stepTitles.map((t, i) => (
                            <React.Fragment key={t}>
                                <span className={`text-xs font-medium px-2 py-0.5 rounded ${step === i + 1 ? 'bg-emerald-900/30 text-emerald-400' : 'text-[#808080]'}`}>{t}</span>
                                {i < stepTitles.length - 1 && <ChevronRight size={12} className="text-[#555]" />}
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        {step > 1 && (
                            <button onClick={() => setStep(step - 1)} className="px-3 py-1.5 text-sm text-[#808080] hover:text-white transition-colors">
                                Back
                            </button>
                        )}
                        {step < 3 ? (
                            <button
                                onClick={() => setStep(step + 1)}
                                disabled={step === 1 ? !canProceedStep1 : !canProceedStep2}
                                className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                            >
                                Next
                            </button>
                        ) : (
                            <button
                                onClick={handleCreate}
                                disabled={creating}
                                className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm rounded-lg transition-colors flex items-center gap-1.5"
                            >
                                {creating && <Loader2 size={14} className="animate-spin" />}
                                Create Pipeline
                            </button>
                        )}
                    </div>
                </div>
            }
        >
            <div className="space-y-4 min-h-[300px]">
                {/* Step 1: Source */}
                {step === 1 && (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-[#cccccc] mb-1">Pipeline Name <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full p-2 border border-[#3c3c3c] rounded-lg bg-[#1e1e1e] text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-[#cccccc] mb-2">Choose Source <span className="text-red-500">*</span></label>
                            <ConnectorCatalog
                                mode="source"
                                selectedId={sourceId}
                                onSelect={(id) => { setSourceId(id); setSourceFields({}); }}
                            />
                        </div>
                        {/* Dynamic credential fields */}
                        {sourceId && isConnectorAvailable(sourceId) && (
                            <ConnectorFields
                                connectorId={sourceId}
                                values={sourceFields}
                                onChange={handleSourceFieldChange}
                            />
                        )}
                        {/* Poll interval for pull sources */}
                        {isPullSource && isConnectorAvailable(sourceId) && (
                            <div className="p-3 bg-[#1e1e1e] rounded-lg border border-[#3c3c3c]">
                                <label className="block text-xs text-[#a0a0a0] mb-1">
                                    <Clock size={10} className="inline mr-1" />
                                    Poll Interval
                                </label>
                                <select
                                    value={sourceFields.poll_interval_minutes || '60'}
                                    onChange={(e) => handleSourceFieldChange('poll_interval_minutes', e.target.value)}
                                    className="w-full p-2 border border-[#3c3c3c] rounded-lg bg-[#252526] text-white text-sm"
                                >
                                    <option value="5">Every 5 minutes</option>
                                    <option value="15">Every 15 minutes</option>
                                    <option value="30">Every 30 minutes</option>
                                    <option value="60">Every hour</option>
                                    <option value="360">Every 6 hours</option>
                                    <option value="1440">Every 24 hours</option>
                                </select>
                            </div>
                        )}
                        {sourceId && !isConnectorAvailable(sourceId) && (
                            <div className="p-3 bg-[#1e1e1e] rounded-lg border border-[#3c3c3c] text-center">
                                <p className="text-xs text-[#808080]">
                                    This connector is <span className="text-yellow-400 font-medium">coming soon</span>. You can still create the pipeline — we&apos;ll notify you when it&apos;s ready.
                                </p>
                            </div>
                        )}
                    </>
                )}

                {/* Step 2: Contract */}
                {step === 2 && (
                    <div>
                        <label className="block text-sm font-medium text-[#cccccc] mb-1">Compare Against Contract <span className="text-red-500">*</span></label>
                        <select
                            value={contractId}
                            onChange={(e) => setContractId(e.target.value)}
                            className="w-full p-2 border border-[#3c3c3c] rounded-lg bg-[#1e1e1e] text-white"
                        >
                            <option value="">-- Select a contract --</option>
                            {contracts.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                        <p className="text-xs text-[#808080] mt-2">
                            Every file ingested by this pipeline will be validated against the selected contract&apos;s schema.
                        </p>
                    </div>
                )}

                {/* Step 3: Destination */}
                {step === 3 && (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-[#cccccc] mb-2">Export Destination <span className="text-[#555]">(optional)</span></label>
                            <ConnectorCatalog
                                mode="destination"
                                selectedId={destId}
                                onSelect={(id) => { setDestId(id === destId ? null : id); setDestFields({}); }}
                            />
                        </div>
                        {destId && isConnectorAvailable(destId) && (
                            <ConnectorFields
                                connectorId={destId}
                                values={destFields}
                                onChange={handleDestFieldChange}
                            />
                        )}
                        {!destId && (
                            <p className="text-xs text-[#808080] italic py-1">
                                No destination selected — pipeline will function as a monitor only (drift detection without automated export).
                            </p>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
}

// ---------------------------------------------------------------------------
// PipelineManager (Main Export)
// ---------------------------------------------------------------------------

export default function PipelineManager({ addToast }) {
    const [pipelines, setPipelines] = useState([]);
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showWizard, setShowWizard] = useState(false);
    const [selectedPipeline, setSelectedPipeline] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'paused'

    // Undo grace period state
    const [pendingDeletions, setPendingDeletions] = useState(new Set());
    const deletionTimeoutsRef = useRef({});

    const fetchData = useCallback(async () => {
        try {
            const [pipelineData, contractData] = await Promise.all([
                getPipelines(),
                getContracts(),
            ]);
            setPipelines(pipelineData);
            setContracts(contractData);
        } catch (err) {
            console.error('Failed to load pipelines:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleToggle = async (id) => {
        try {
            await togglePipeline(id);
            fetchData();
        } catch { addToast('Failed to toggle pipeline', 'error'); }
    };

    const handleDelete = useCallback((id) => {
        const pipeline = pipelines.find(p => p.id === id);
        if (!pipeline) return;

        // Optimistic UI hide
        setPendingDeletions(prev => new Set(prev).add(id));

        const timeoutId = setTimeout(async () => {
            try {
                await deletePipeline(id);
                setPendingDeletions(prev => {
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
                if (selectedPipeline?.id === id) setSelectedPipeline(null);
                fetchData();
            } catch (error) {
                console.error(error);
                addToast('Failed to delete pipeline', 'error');
                // Revert optimistic hide
                setPendingDeletions(prev => {
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
            }
        }, 2000);

        deletionTimeoutsRef.current[id] = timeoutId;

        addToast(`Pipeline "${pipeline.name}" deleted`, 'success', {
            duration: 2000,
            action: {
                label: 'Undo',
                onClick: () => {
                    clearTimeout(deletionTimeoutsRef.current[id]);
                    delete deletionTimeoutsRef.current[id];
                    setPendingDeletions(prev => {
                        const next = new Set(prev);
                        next.delete(id);
                        return next;
                    });
                    addToast('Deletion undone', 'info');
                }
            }
        });
    }, [pipelines, fetchData, addToast, selectedPipeline]);

    const handleDetailUpdate = (updated) => {
        setPipelines(prev => prev.map(p => p.id === updated.id ? updated : p));
        setSelectedPipeline(updated);
    };

    // Filtered pipelines
    const filteredPipelines = useMemo(() => {
        return pipelines.filter(p => !pendingDeletions.has(p.id)).filter(p => {
            if (searchQuery) {
                const q = searchQuery.toLowerCase();
                const sourceName = getConnectorById(p.source?.type || p.source?.provider)?.name || '';
                if (!p.name.toLowerCase().includes(q) && !sourceName.toLowerCase().includes(q)) return false;
            }
            if (statusFilter === 'active' && !p.is_active) return false;
            if (statusFilter === 'paused' && p.is_active) return false;
            return true;
        });
    }, [pipelines, pendingDeletions, searchQuery, statusFilter]);

    const connectorCount = CONNECTORS.length;

    return (
        <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-xl font-bold text-white">Pipelines</h2>
                </div>
                <button
                    onClick={() => setShowWizard(true)}
                    className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                    <Plus size={16} />
                    New Pipeline
                </button>
            </div>

            {/* Search & Filter Bar */}
            {pipelines.length > 0 && (
                <div className="flex items-center gap-3 mb-4">
                    <div className="relative flex-1">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#808080]" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search pipelines..."
                            className="w-full pl-9 pr-3 py-2 border border-[#3c3c3c] rounded-lg bg-[#1e1e1e] text-white text-sm placeholder-[#555]"
                        />
                    </div>
                    <div className="flex gap-1">
                        {[
                            { id: 'all', label: 'All' },
                            { id: 'active', label: 'Active' },
                            { id: 'paused', label: 'Paused' },
                        ].map(f => (
                            <button
                                key={f.id}
                                onClick={() => setStatusFilter(f.id)}
                                className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${statusFilter === f.id
                                    ? 'bg-[#3c3c3c] text-white'
                                    : 'text-[#808080] hover:text-[#cccccc]'
                                    }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Content */}
            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 size={24} className="animate-spin text-[#808080]" />
                </div>
            ) : pipelines.length === 0 ? (
                <div className="text-center py-16 bg-[#252526] rounded-lg border border-[#3c3c3c]">


                    <h3 className="text-lg font-medium text-white tracking-tight mb-2">No pipelines yet</h3>
                    <p className="text-sm text-[#808080] max-w-md mx-auto mb-4">
                        Pipelines connect your data sources to DriftOps contracts and optional export destinations in one unified flow.
                    </p>
                    <p className="text-xs text-[#555] mb-5">{connectorCount} connectors available</p>
                    <button
                        onClick={() => setShowWizard(true)}
                        className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                        <Plus size={16} />
                        Create Your First Pipeline
                    </button>
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredPipelines.map((p) => (
                        <PipelineCard
                            key={p.id}
                            pipeline={p}
                            onToggle={handleToggle}
                            onDelete={handleDelete}
                            onClick={() => setSelectedPipeline(p)}
                            addToast={addToast}
                        />
                    ))}
                    {filteredPipelines.length === 0 && pipelines.length > 0 && (
                        <p className="text-center text-sm text-[#808080] py-8">No pipelines match your search.</p>
                    )}
                </div>
            )}

            {/* Create Wizard */}
            <CreatePipelineWizard
                isOpen={showWizard}
                onClose={() => setShowWizard(false)}
                onCreated={fetchData}
                contracts={contracts}
                addToast={addToast}
            />

            {/* Detail Panel */}
            {selectedPipeline && (
                <PipelineDetailPanel
                    pipeline={selectedPipeline}
                    onClose={() => setSelectedPipeline(null)}
                    onUpdate={handleDetailUpdate}
                    addToast={addToast}
                />
            )}
        </div>
    );
}
